import { BASE_URL } from 'config';
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Grid,
  Divider,
  Paper,
  Chip,
  Tooltip,
  IconButton,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DownloadIcon from "@mui/icons-material/Download";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ArrowBackIcon from "@mui/icons-material/ArrowBack"; // إضافة أيقونة السهم
import { PrimaryColor } from "assets/theme/base/themeColors";

const getFileType = (url) => {
  if (!url) return null;
  const ext = url.split(".").pop().toLowerCase();
  if (["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(ext)) return "image";
  if (["pdf"].includes(ext)) return "pdf";
  if (["doc", "docx"].includes(ext)) return "word";
  return "file";
};

const DocumentPreview = ({ url, label }) => {
  if (!url) return <Typography color="text.secondary">لا يوجد</Typography>;
  const type = getFileType(url);
  if (type === "image") {
    return (
      <>
        <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
          <img
            src={url}
            alt={label}
            style={{ maxWidth: 220, maxHeight: 180, borderRadius: 8, border: "2px solid #eee" }}
          />
          <Button
            variant="outlined"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<DownloadIcon />}
            sx={{ color: "black !important" }}
          >
            تنزيل
          </Button>
        </Box>
      </>
    );
  }
  if (type === "pdf") {
    return (
      <>
        <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
          <Tooltip title="معاينة PDF">
            <IconButton href={url} target="_blank" rel="noopener noreferrer" color="error">
              <PictureAsPdfIcon sx={{ fontSize: 60 }} />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            color="error"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<DownloadIcon />}
            sx={{ color: "black !important" }}
          >
            تنزيل
          </Button>
        </Box>
      </>
    );
  }
  if (type === "word") {
    return (
      <>
        <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
          <Tooltip title="معاينة ملف Word">
            <IconButton href={url} target="_blank" rel="noopener noreferrer" color="info">
              <InsertDriveFileIcon sx={{ fontSize: 60 }} />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            color="info"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<DownloadIcon />}
            sx={{ color: "black !important" }}
          >
            تنزيل
          </Button>
        </Box>
      </>
    );
  }
  // Generic file
  return (
    <Button
      variant="outlined"
      color="secondary"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      startIcon={<DownloadIcon />}
      sx={{ color: "black !important" }}
    >
      تنزيل الملف
    </Button>
  );
};

export default function StoreDocumntationPreview() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    const token = localStorage.getItem("user-token");
    axios
      .get(`${BASE_URL}/administrator/store-documents/show/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setData(res.data.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [id]);
  // accept or reject Store Documntation
  const handleAction = async (status) => {
    const statusText = status === 2 ? "قبول" : "رفض";
    try {
      const token = localStorage.getItem("user-token");
      Swal.fire({
        title: `جاري إرسال طلب ${statusText} الوثيقة...`,
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });
      await axios.get(
        `${BASE_URL}/administrator/store-documents/status/${id}/${status}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      Swal.close();
      Swal.fire({
        icon: "success",
        title: `تم ${statusText} الوثيقة!`,
        text: `تم ${statusText} الوثيقة بنجاح`,
      }).then(() => navigate(-1));
    } catch (error) {
      Swal.close();
      Swal.fire({
        icon: "error",
        title: `فشل ${statusText} الوثيقة!`,
        text: error.response?.data?.message || error.message || "حدث خطأ أثناء العملية",
      });
    }
  };

  if (loading) {
    return (
      <>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} sx={{ color: "#ca9937" }} />
        </Box>
      </>
    );
  }

  if (!data) {
    return (
      <>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Typography color="error">تعذر تحميل بيانات الوثيقة</Typography>
        </Box>
      </>
    );
  }

  // ألوان مخصصة
  const acceptColor = "#43a047";
  const rejectColor = "#e53935";
  const sectionBg = "#f9f9f9";
  const sectionBorder = "#e0e0e0";

  // بيانات المتجر
  const store = data.store || {};
  const location = store.location_hierarchy || {};

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        borderRadius: 4,
        boxShadow: 3,
        p: 4,
        maxWidth: 1000,
        mx: "auto",
        mt: 4,
      }}
    >
      {/* زر العودة */}
      <Box mb={2} display="flex" justifyContent="flex-end">
        <IconButton
          onClick={() => navigate("/storeDocumntation")}
          sx={{
            fontSize: 28,
            background: PrimaryColor,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "rgba(0, 0, 0, 0)",
            backgroundClip: "text",
            color: "rgba(0, 0, 0, 0)",
          }}
        >
          <ArrowBackIcon fontSize="inherit" />
        </IconButton>
      </Box>
      <Typography variant="h4" fontWeight="bold" mb={3} align="center" color="#ca9937">
        مراجعة بيانات المتجر والوثائق
      </Typography>
      <Divider sx={{ mb: 3 }} />
      {/* بيانات المتجر */}
      <Paper
        elevation={2}
        sx={{ bgcolor: sectionBg, border: `1px solid ${sectionBorder}`, p: 3, mb: 4 }}
      >
        <Typography variant="h6" fontWeight="bold" mb={2} color="#3b3b3b">
          بيانات المتجر
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <Typography fontWeight="bold">اسم المتجر:</Typography>
            <Typography>{store.name_ar || "-"}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography fontWeight="bold">اسم المتجر بالإنجليزية:</Typography>
            <Typography>{store.name_en || "-"}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography fontWeight="bold">رقم الهاتف:</Typography>
            <Typography>{store.phone || "-"}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={8}>
            <Typography fontWeight="bold">العنوان:</Typography>
            <Typography>
              {[
                store.street,
                store.city || location.province?.name,
                location.state?.name,
                location.country?.name,
              ]
                .filter(Boolean)
                .join(" | ") || "-"}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography fontWeight="bold">الرقم الموحد:</Typography>
            <Typography>{data.unified_number || "-"}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography fontWeight="bold">رقم السجل التجاري:</Typography>
            <Typography>{data.commercial_registration_number || "-"}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography fontWeight="bold">تاريخ انتهاء السجل التجاري:</Typography>
            <Typography>{data.cr_expiry_date ? data.cr_expiry_date.split(" ")[0] : "-"}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography fontWeight="bold">اسم النشاط التجاري:</Typography>
            <Typography>{data.business_name || "-"}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography fontWeight="bold">رقم رخصة البلدية:</Typography>
            <Typography>{data.municipal_license_number || "-"}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography fontWeight="bold">تاريخ انتهاء رخصة البلدية:</Typography>
            <Typography>
              {data.municipal_license_expiry ? data.municipal_license_expiry.split(" ")[0] : "-"}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography fontWeight="bold">رقم ضريبة القيمة المضافة:</Typography>
            <Typography>{data.tax_number || "-"}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography fontWeight="bold">رقم الهاتف للمالك:</Typography>
            <Typography>{data.owner_phone || "-"}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography fontWeight="bold">تاريخ الإنشاء:</Typography>
            <Typography>{store.created_at ? store.created_at.split("T")[0] : "-"}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography fontWeight="bold">آخر تحديث:</Typography>
            <Typography>{store.updated_at ? store.updated_at.split("T")[0] : "-"}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography fontWeight="bold">الحالة:</Typography>
            <Chip
              label={data.status === 2 ? "مقبولة" : data.status === 3 ? "مرفوضة" : "قيد المراجعة"}
              color={data.status === 2 ? "success" : data.status === 3 ? "error" : "warning"}
              sx={{ fontWeight: "bold" }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography fontWeight="bold">البريد الإلكتروني:</Typography>
            <Typography>{store.email || "-"}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography fontWeight="bold">الرمز البريدي:</Typography>
            <Typography>{store.zip_code || "-"}</Typography>
          </Grid>
          {/* شعار المتجر */}
          {store.logo && (
            <Grid item xs={12} sm={6} md={4}>
              <Typography fontWeight="bold">شعار المتجر:</Typography>
              <img
                src={
                  store.logo.startsWith("http")
                    ? store.logo
                    : `https://back.al-balad.sa/uploads/stores/logos/${store.logo}`
                }
                alt="شعار المتجر"
                style={{
                  maxWidth: 120,
                  maxHeight: 120,
                  borderRadius: 12,
                  border: "2px solid #eee",
                }}
              />
            </Grid>
          )}
          {/* الموقع على الخريطة */}
          {store.location && (
            <Grid item xs={12}>
              <Typography fontWeight="bold">الموقع على الخريطة:</Typography>
              <Box mt={1} sx={{ borderRadius: 2, overflow: "hidden", border: "1px solid #eee" }}>
                <div dangerouslySetInnerHTML={{ __html: store.location }} />
              </Box>
            </Grid>
          )}
        </Grid>
      </Paper>
      {/* بيانات الوثائق */}
      <Paper
        elevation={2}
        sx={{ bgcolor: sectionBg, border: `1px solid ${sectionBorder}`, p: 3, mb: 4 }}
      >
        <Typography variant="h6" fontWeight="bold" mb={2} color="#3b3b3b">
          وثائق المتجر
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Typography fontWeight="bold" mb={1}>
              السجل التجاري
            </Typography>
            <DocumentPreview url={data.cr_file_url} label="السجل التجاري" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography fontWeight="bold" mb={1}>
              رخصة البلدية
            </Typography>
            <DocumentPreview url={data.license_file_url} label="رخصة البلدية" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography fontWeight="bold" mb={1}>
              العنوان الوطني
            </Typography>
            <DocumentPreview url={data.national_address_file_url} label="العنوان الوطني" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography fontWeight="bold" mb={1}>
              شهادة الضريبة
            </Typography>
            <DocumentPreview url={data.tax_file_url} label="شهادة الضريبة" />
          </Grid>
        </Grid>
      </Paper>
      {/* أزرار القبول والرفض */}
      <Box display="flex" justifyContent="center" gap={3} mt={2}>
        <Button
          variant="contained"
          sx={{
            minWidth: 140,
            fontWeight: "bold",
            bgcolor: acceptColor,
            color: "#fff",
            "&:hover": { bgcolor: "#388e3c" },
          }}
          onClick={() => handleAction(2)}
          startIcon={<CheckCircleIcon sx={{ fontSize: 26 }} />}
        >
          قبول
        </Button>
        <Button
          variant="contained"
          sx={{
            minWidth: 140,
            fontWeight: "bold",
            bgcolor: rejectColor,
            color: "#fff",
            "&:hover": { bgcolor: "#b71c1c" },
          }}
          onClick={() => handleAction(3)}
          startIcon={<CancelIcon sx={{ fontSize: 26 }} />}
        >
          رفض
        </Button>
      </Box>
    </Box>
  );
}
